/**
 * 69.58 step 2, the look (ARCHITECTURE §5.29; the sheet design_sheets/countermeasures.html; the sound audio/countermeasureMix.ts):
 * a ship's countermeasures seen. Nothing in play draws them yet: a flare (state.decoys, core/decoys.ts `flareAt`) is heard
 * but never seen, and the roof turret's bursts (`shipIntercept`) are silent to the eye.
 *   - **The flares** (`FlareField`): each a burning white-magnesium point and its bloom (both at least a few thousandths of a
 *     radian across, so a flare a long way off still reads), the smoke it leaves lingering behind its fall, the dispenser's
 *     puff at the pop; a flare's burn fades out over its last moments. Two looks: `magnesium`, a big white bloom and a thick
 *     white trail that hangs for seconds (the real thing's); `sparkler`, a smaller hotter point that flickers and sheds
 *     burning bits, a thin grey trail.
 *   - **The roof turret's tracers** (`TURRET_TRACERS`), drawn as point defence's are (game/actionHost.ts PD_TRACERS: rounds
 *     streaking from the drawn muzzle, a miss's rounds flying on to their self-destruct, `drawAirburst`): `stream` point
 *     defence's CIWS as the ground's mounts draw it, `sparse` fewer and longer (one round in five a tracer).
 *   - **A kill** (`drawTurretKill`): `burst`, the LAW's burst in the air (as point defence draws a rocket it killed: the
 *     flash, the fire, a dark puff, its pieces falling); `flak`, a bigger dark puff that hangs and a flash.
 *   - **A missile on a flare**: its own burst (the blast's LAW class, drawn by the host's burst); `scatter` adds the flare's
 *     magnesium thrown out burning (`FlareField.scatter`), `burst` adds nothing.
 * Cost: three instanced meshes (three draws) for every flare at once — the cores and burning bits, the blooms, the smoke —
 * written once a frame; no allocation a flare. Presentation only: reads what it is given, writes nothing back.
 */
import * as THREE from 'three';
import type { Vec3 } from '../types';
import { COUNTERMEASURES } from '../constants/action';
import type { ExplosionField } from './explosionLook';

export type FlareLook = 'magnesium' | 'sparkler';
export const FLARE_LOOK_STYLES: readonly FlareLook[] = ['magnesium', 'sparkler'];
export interface FlareLookNumbers {
  /** the burning point: across (people), at least `minAngle` radians from the camera; its colour */
  core: number; minAngle: number; color: string;
  /** the bloom round it: across (people), at least `bloomAngle` radians; its colour; how much it flickers (a share) and how fast (Hz) */
  bloom: number; bloomAngle: number; bloomColor: string; flicker: number; flickerHz: number;
  /** the smoke behind it: a puff every `every` s, `s0` → `s1` people across over `life` s, `alpha` at its thickest, its colour, rising `rise` people a second */
  trail: { every: number; life: number; s0: number; s1: number; alpha: number; color: string; rise: number };
  /** burning bits shed a second, each `size` people across, falling `fall` people a second², living `life` s */
  sparks: { rate: number; life: number; fall: number; size: number };
  /** the dispenser's puff at the pop: its flash (people across, s), its smoke puffs (how many, people across, s, colour) */
  pop: { flash: number; flashLife: number; puffs: number; size: number; life: number; color: string };
}
export const FLARE_LOOKS: Record<FlareLook, FlareLookNumbers> = {
  magnesium: {
    core: 0.45, minAngle: 0.0025, color: '#ffffff', bloom: 4, bloomAngle: 0.02, bloomColor: '#fff0d6', flicker: 0.15, flickerHz: 23,
    trail: { every: 0.03, life: 7, s0: 0.35, s1: 3, alpha: 0.6, color: '#eeede8', rise: 0.12 },
    sparks: { rate: 0, life: 0.7, fall: 6, size: 0.1 },
    pop: { flash: 1.2, flashLife: 0.08, puffs: 3, size: 0.8, life: 1.6, color: '#a8a6a0' },
  },
  sparkler: {
    core: 0.3, minAngle: 0.0018, color: '#fff4d8', bloom: 2.2, bloomAngle: 0.012, bloomColor: '#ffb868', flicker: 0.4, flickerHz: 31,
    trail: { every: 0.05, life: 3.5, s0: 0.2, s1: 1.4, alpha: 0.4, color: '#c4c2bc', rise: 0.05 },
    sparks: { rate: 30, life: 0.7, fall: 6, size: 0.12 },
    pop: { flash: 0.8, flashLife: 0.06, puffs: 2, size: 0.5, life: 1.2, color: '#8e8c86' },
  },
};
/** A flare's burn fades out over its last `fadeSeconds` (the sim's burn: COUNTERMEASURES.burnSeconds). */
export const FLARE_FADE_SECONDS = 0.35;
/** The share of a flare's drawn size at `age` seconds into its burn: whole, then fading out at its end; 0 past it. */
export function flareBurn(age: number): number {
  const B = COUNTERMEASURES.burnSeconds;
  return age < 0 || age >= B ? 0 : Math.min(1, (B - age) / FLARE_FADE_SECONDS);
}

/** The roof turret's tracers: a burst's `tracers` rounds `spacing` s apart at `speed` people a second, each `streak` people long; a miss flies on past by `past` of the range. */
export type TracerLook = 'stream' | 'sparse';
export const TRACER_LOOK_STYLES: readonly TracerLook[] = ['stream', 'sparse'];
export const TURRET_TRACERS: Record<TracerLook, { tracers: number; spacing: number; speed: number; streak: number; past: number }> = {
  stream: { tracers: 4, spacing: 0.045, speed: 450, streak: 3, past: 0.35 },   // (point defence's CIWS: game/actionHost.ts PD_TRACERS)
  sparse: { tracers: 2, spacing: 0.09, speed: 450, streak: 5, past: 0.5 },
};
/** A missed round's self-destruct (point defence's, 67.78: game/actionHost.ts PD_AIRBURST): a flash and a dark puff at least `minAngle` across from the camera. */
export const AIRBURST_LOOK = { scatter: 0.02, minAngle: 0.004, minPeople: 0.6, flash: 0.09, puff: 1.4 } as const;
export type KillLook = 'burst' | 'flak';
export const KILL_LOOK_STYLES: readonly KillLook[] = ['burst', 'flak'];
export type FlareBurstLook = 'burst' | 'scatter';
export const FLARE_BURST_LOOK_STYLES: readonly FlareBurstLook[] = ['burst', 'scatter'];

/** The owner's pick for the flares (DECISIONS 291: "lean sparkler for countermeasures"); the tracers, the kill and a burst on a flare as leaned. */
export const COUNTERMEASURE_LOOK: { flare: FlareLook; tracer: TracerLook; kill: KillLook; flareBurst: FlareBurstLook } = { flare: 'sparkler', tracer: 'stream', kill: 'burst', flareBurst: 'scatter' };

/** A self-destructing round's burst at `at` (up `up`) seen from `cam`: the flash and the dark puff. */
export function drawAirburst(fx: ExplosionField, at: Vec3, up: Vec3, cam: Vec3, person: number): void {
  const A = AIRBURST_LOOK, size = Math.max(A.minPeople * person, Math.hypot(at.x - cam.x, at.y - cam.y, at.z - cam.z) * A.minAngle);
  fx.emit('fireball', { at, up, life: A.flash, s0: 0.5 * size, s1: size, c0: '#ffe2a0', c1: '#ff9a40' });
  fx.emit('smoke', { at, up, life: A.puff, s0: 0.35 * size, s1: 1.2 * size, c0: '#4a4844', c1: '#77746e', drag: 1, spin: 0.3 });
}

/** A missile the turret killed, at `at` (up `up`, the ground under it `ground`), drawn into `fx` in `look`. */
export function drawTurretKill(fx: ExplosionField, look: KillLook, at: Vec3, up: Vec3, ground: Vec3, person: number, seed: number, rnd: () => number = Math.random): void {
  if (look === 'burst') fx.spawn('law', at, up, 'rock', 'far', seed);
  else {
    fx.emit('flash', { at, up, life: 0.08, s0: 1.2 * person, s1: 1.6 * person, c0: '#fff4d0' });
    fx.emit('fireball', { at, up, life: 0.2, s0: 0.6 * person, s1: 1.3 * person, c0: '#ffd27a', c1: '#ff7a30' });
    for (let k = 0; k < 3; k++) {
      const j = { x: at.x + (rnd() - 0.5) * person, y: at.y + (rnd() - 0.5) * person, z: at.z + (rnd() - 0.5) * person };
      fx.emit('smoke', { at: j, up, life: 4, s0: 0.8 * person, s1: 3.2 * person, c0: '#3c3a38', c1: '#5c5a56', drag: 1.2, spin: 0.2, delay: 0.04 * k });
    }
  }
  // its pieces falling
  for (let k = 0; k < 8; k++) {
    const a = k * 2.4, out = 3 * person * rnd();
    fx.emit('debris', { at, up, ground, vel: { x: up.x * (1 + rnd() * 3) * person + Math.cos(a) * out, y: up.y * (1 + rnd() * 3) * person, z: up.z * (1 + rnd() * 3) * person + Math.sin(a) * out }, gravity: 9 * person, drag: 0.3, life: 1.6, s0: 0.12 * person, s1: 0.1 * person, c0: '#3a3a3a', spin: 6 });
  }
}

/** A flare drawn this frame: its id, where it is, how long it has burned (s), the up there (from its planet). */
export interface FlareSeen { id: number; pos: Vec3; age: number; up: Vec3 }

const CAP = { glow: 192, bloom: 48, puff: 900 } as const;
const _m = new THREE.Matrix4(), _q = new THREE.Quaternion(), _s = new THREE.Vector3(), _p = new THREE.Vector3(), _c = new THREE.Color();
interface Bit { alive: boolean; pos: THREE.Vector3; vel: THREE.Vector3; up: THREE.Vector3; age: number; life: number; size: number; fall: number; smoke: number }
interface Puff { alive: boolean; pos: THREE.Vector3; vel: THREE.Vector3; age: number; life: number; s0: number; s1: number; alpha: number }

/** A soft round spot (white, its alpha falling off from the middle): the bloom's texture, built without a canvas. */
function spot(n = 64): THREE.DataTexture {
  const data = new Uint8Array(n * n * 4);
  for (let y = 0; y < n; y++) for (let x = 0; x < n; x++) {
    const dx = (x + 0.5) / n * 2 - 1, dy = (y + 0.5) / n * 2 - 1, r = Math.min(1, Math.hypot(dx, dy));
    const a = Math.pow(1 - r, 2.2), i = (y * n + x) * 4;
    data[i] = data[i + 1] = data[i + 2] = 255; data[i + 3] = Math.round(255 * a);
  }
  const t = new THREE.DataTexture(data, n, n, THREE.RGBAFormat);
  t.needsUpdate = true;
  return t;
}

/**
 * Every flare at once: give it the flares burning this frame (`update`), the pops as they happen (`pop`), a missile's burst
 * on one (`scatter`). Its own pools (a lingering trail would starve the explosions' smoke): three instanced meshes.
 */
export class FlareField {
  private readonly glow: THREE.InstancedMesh;
  private readonly bloom: THREE.InstancedMesh;
  private readonly smoke: THREE.InstancedMesh;
  private readonly alpha: THREE.InstancedBufferAttribute;
  private readonly bits: Bit[] = [];
  private readonly puffs: Puff[] = [];
  private readonly flashes: Array<{ pos: THREE.Vector3; age: number; life: number; size: number }> = [];
  private readonly trails = new Map<number, { last: THREE.Vector3; acc: number; spark: number }>();
  private readonly tex: THREE.DataTexture;
  private nextPuff = 0;
  private time = 0;
  readonly L: FlareLookNumbers;

  constructor(private readonly scene: THREE.Scene, private readonly person: number, readonly look: FlareLook = COUNTERMEASURE_LOOK.flare) {
    this.L = FLARE_LOOKS[look];
    const ico = new THREE.IcosahedronGeometry(1, 1);
    this.glow = new THREE.InstancedMesh(ico, new THREE.MeshBasicMaterial({ transparent: true, blending: THREE.AdditiveBlending, depthWrite: false, toneMapped: false }), CAP.glow);
    this.glow.name = 'flares:glow';
    this.tex = spot();
    this.bloom = new THREE.InstancedMesh(new THREE.PlaneGeometry(1, 1), new THREE.MeshBasicMaterial({ map: this.tex, color: this.L.bloomColor, transparent: true, blending: THREE.AdditiveBlending, depthWrite: false, toneMapped: false }), CAP.bloom);
    this.bloom.name = 'flares:bloom';
    // the smoke: lit, see-through, each puff's own fade (an instanced alpha: the shared material has one opacity)
    const smokeMat = new THREE.MeshLambertMaterial({ color: '#ffffff', emissive: '#3a3a3a', transparent: true, depthWrite: false });
    smokeMat.onBeforeCompile = s => {
      s.vertexShader = 'attribute float aFade;\nvarying float vFade;\n' + s.vertexShader.replace('#include <begin_vertex>', '#include <begin_vertex>\n  vFade = aFade;');
      s.fragmentShader = 'varying float vFade;\n' + s.fragmentShader.replace('#include <opaque_fragment>', '#include <opaque_fragment>\n  gl_FragColor.a *= vFade;');
    };
    smokeMat.customProgramCacheKey = () => 'flareSmoke';
    const puffGeo = new THREE.IcosahedronGeometry(1, 1);
    this.alpha = new THREE.InstancedBufferAttribute(new Float32Array(CAP.puff), 1).setUsage(THREE.DynamicDrawUsage);
    puffGeo.setAttribute('aFade', this.alpha);
    this.smoke = new THREE.InstancedMesh(puffGeo, smokeMat, CAP.puff);
    this.smoke.name = 'flares:smoke';
    for (const m of [this.smoke, this.glow, this.bloom]) { m.count = 0; m.frustumCulled = false; scene.add(m); }
    this.bloom.renderOrder = 2;
    _c.set(this.L.trail.color);
    for (let i = 0; i < CAP.puff; i++) this.smoke.setColorAt(i, _c);
  }

  /** Pieces alive now (for a sheet's count and the cost probe). */
  get live(): number { return this.bits.filter(b => b.alive).length + this.puffs.filter(p => p.alive).length + this.flashes.length; }

  /** The dispenser fired at `at` (up `up`), its cartridge thrown toward `dir`: a flash and a little smoke. */
  pop(at: Vec3, up: Vec3, dir: Vec3): void {
    const P = this.L.pop, k = this.person;
    this.flashes.push({ pos: new THREE.Vector3(at.x, at.y, at.z), age: 0, life: P.flashLife, size: P.flash * k });
    for (let i = 0; i < P.puffs; i++) {
      const v = 0.6 + 0.5 * i;
      this.puff(at, { x: dir.x * v * k + up.x * 0.2 * k, y: dir.y * v * k + up.y * 0.2 * k, z: dir.z * v * k + up.z * 0.2 * k }, P.life, 0.4 * P.size * k, P.size * k * 1.8, 0.7, P.color);
    }
  }

  /** A missile burst on a flare at `at` (up `up`): the flare's magnesium thrown out, burning as it falls. */
  scatter(at: Vec3, up: Vec3, rnd: () => number = Math.random): void {
    const k = this.person;
    for (let i = 0; i < 14; i++) {
      const a = rnd() * Math.PI * 2, b = rnd() * 2 - 1, r = Math.sqrt(1 - b * b), sp = (4 + 6 * rnd()) * k;
      this.bit(at, { x: Math.cos(a) * r * sp, y: b * sp, z: Math.sin(a) * r * sp }, up, 0.9 + 0.8 * rnd(), 0.16 * k, 9 * k, 0.06);
    }
  }

  /** This frame's flares (`seen`), the camera, the frame's `dt`: everything aged and written. */
  update(seen: readonly FlareSeen[], camera: THREE.Camera, dt: number): void {
    this.time += dt;
    const L = this.L, k = this.person, cam = camera.position;
    // the trails and the burning bits shed: from each flare's last drawn place to this one
    for (const f of seen) {
      const burn = flareBurn(f.age);
      let t = this.trails.get(f.id);
      if (!t) { t = { last: new THREE.Vector3(f.pos.x, f.pos.y, f.pos.z), acc: 0, spark: 0 }; this.trails.set(f.id, t); }
      if (burn > 0) {
        t.acc += dt; t.spark += dt * L.sparks.rate;
        const n = Math.floor(t.acc / L.trail.every);
        for (let i = 0; i < n; i++) {
          const u = (i + 1) / n, at = { x: t.last.x + (f.pos.x - t.last.x) * u, y: t.last.y + (f.pos.y - t.last.y) * u, z: t.last.z + (f.pos.z - t.last.z) * u };
          const r = L.trail.rise * k;
          this.puff(at, { x: f.up.x * r, y: f.up.y * r, z: f.up.z * r }, L.trail.life, L.trail.s0 * k, L.trail.s1 * k, L.trail.alpha * burn, L.trail.color);
        }
        t.acc -= n * L.trail.every;
        for (; t.spark >= 1; t.spark -= 1) {
          const a = this.time * 17.3 + t.spark * 5 + f.id, s = 1.5 * k;
          this.bit(f.pos, { x: Math.cos(a) * s, y: Math.sin(a * 1.3) * s, z: Math.sin(a) * s }, f.up, L.sparks.life, L.sparks.size * k, L.sparks.fall * k, 0);
        }
      }
      t.last.set(f.pos.x, f.pos.y, f.pos.z);
    }
    for (const id of this.trails.keys()) if (!seen.some(f => f.id === id)) this.trails.delete(id);
    // the cores, the burning bits, the flashes: one instanced glow
    let g = 0, b = 0;
    _q.identity();
    const put = (at: { x: number; y: number; z: number }, size: number, color: string) => {
      if (g >= CAP.glow || !(size > 0)) return;
      this.glow.setMatrixAt(g, _m.compose(_p.set(at.x, at.y, at.z), _q, _s.setScalar(size / 2)));
      this.glow.setColorAt(g++, _c.set(color));
    };
    for (const f of seen) {
      const burn = flareBurn(f.age);
      if (!(burn > 0)) continue;
      const d = Math.hypot(f.pos.x - cam.x, f.pos.y - cam.y, f.pos.z - cam.z);
      const flick = 1 + L.flicker * 0.5 * (Math.sin(this.time * L.flickerHz * 6.283 + f.id * 1.7) + Math.sin(this.time * L.flickerHz * 4.1 + f.id));
      put(f.pos, Math.max(L.core * k, d * L.minAngle) * burn * (0.85 + 0.15 * flick), L.color);
      if (b < CAP.bloom) {
        const size = Math.max(L.bloom * k, d * L.bloomAngle) * burn * flick;
        this.bloom.setMatrixAt(b++, _m.compose(_p.set(f.pos.x, f.pos.y, f.pos.z), camera.quaternion, _s.set(size, size, 1)));
      }
    }
    for (const q of this.bits) {
      if (!q.alive) continue;
      q.age += dt;
      if (q.age >= q.life) { q.alive = false; continue; }
      q.vel.addScaledVector(q.up, -q.fall * dt).multiplyScalar(Math.exp(-1.2 * dt));
      q.pos.addScaledVector(q.vel, dt);
      if (q.smoke > 0) {
        q.smoke -= dt;
        if (q.smoke <= 0) { q.smoke += 0.06; this.puff(q.pos, { x: 0, y: 0, z: 0 }, 1.6, 0.15 * k, 0.8 * k, 0.45 * (1 - q.age / q.life), L.trail.color); }
      }
      put(q.pos, q.size * (1 - 0.6 * q.age / q.life), q.age < 0.25 * q.life ? '#ffffff' : '#ffd8a0');
    }
    for (let i = this.flashes.length - 1; i >= 0; i--) {
      const f = this.flashes[i];
      f.age += dt;
      if (f.age >= f.life) { this.flashes.splice(i, 1); continue; }
      put(f.pos, f.size * (1 - 0.5 * f.age / f.life), '#fff2c8');
    }
    this.glow.count = g; this.bloom.count = b;
    this.glow.instanceMatrix.needsUpdate = true; if (this.glow.instanceColor) this.glow.instanceColor.needsUpdate = true;
    this.bloom.instanceMatrix.needsUpdate = true;
    // the smoke: swelling, drifting, thinning out
    let n = 0;
    for (let i = 0; i < this.puffs.length; i++) {
      const p = this.puffs[i];
      if (p.alive) {
        p.age += dt;
        if (p.age >= p.life) p.alive = false;
        else { p.vel.multiplyScalar(Math.exp(-0.6 * dt)); p.pos.addScaledVector(p.vel, dt); }
      }
      const u = p.alive ? p.age / p.life : 1;
      const size = p.alive ? p.s0 + (p.s1 - p.s0) * (1 - (1 - u) * (1 - u)) : 0;
      this.smoke.setMatrixAt(i, _m.compose(p.pos, _q, _s.setScalar(size / 2)));
      this.alpha.setX(i, p.alive ? p.alpha * Math.min(1, u / 0.04) * Math.pow(1 - u, 1.3) : 0);
      n = i + 1;
    }
    this.smoke.count = n;
    this.smoke.instanceMatrix.needsUpdate = true; this.alpha.needsUpdate = true;
    if (this.smoke.instanceColor) this.smoke.instanceColor.needsUpdate = true;
  }

  dispose(): void {
    for (const m of [this.glow, this.bloom, this.smoke]) { this.scene.remove(m); m.geometry.dispose(); (m.material as THREE.Material).dispose(); m.dispose(); }
    this.tex.dispose();
  }

  private puff(at: Vec3, vel: Vec3, life: number, s0: number, s1: number, alpha: number, color: string): void {
    let i = -1;
    if (this.puffs.length < CAP.puff) { i = this.puffs.length; this.puffs.push({ alive: false, pos: new THREE.Vector3(), vel: new THREE.Vector3(), age: 0, life: 1, s0: 0, s1: 0, alpha: 0 }); }
    else { for (let q = 0; q < CAP.puff; q++) { const j = (this.nextPuff + q) % CAP.puff; if (!this.puffs[j].alive) { i = j; break; } } if (i < 0) i = this.nextPuff; }
    this.nextPuff = (i + 1) % CAP.puff;
    const p = this.puffs[i];
    p.alive = true; p.age = 0; p.life = life; p.s0 = s0; p.s1 = s1; p.alpha = alpha;
    p.pos.set(at.x, at.y, at.z); p.vel.set(vel.x, vel.y, vel.z);
    this.smoke.setColorAt(i, _c.set(color));
  }

  private bit(at: Vec3, vel: Vec3, up: Vec3, life: number, size: number, fall: number, smoke: number): void {
    let q = this.bits.find(x => !x.alive);
    if (!q) { if (this.bits.length >= CAP.glow) return; q = { alive: false, pos: new THREE.Vector3(), vel: new THREE.Vector3(), up: new THREE.Vector3(), age: 0, life: 1, size: 0, fall: 0, smoke: 0 }; this.bits.push(q); }
    q.alive = true; q.age = 0; q.life = life; q.size = size; q.fall = fall; q.smoke = smoke;
    q.pos.set(at.x, at.y, at.z); q.vel.set(vel.x, vel.y, vel.z); q.up.set(up.x, up.y, up.z);
  }
}
